import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Mail, Phone, ShieldCheck, X } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { apiUrl, getDeviceId } from '../../api/client'
import { ROLE_HOME_PATHS } from '../../components/layout/roleConfig'
import { platformBrandVars } from '../../components/layout/useVertical'
import type {
  ForgotPasswordResponse,
  LoginResponse,
  SendOtpResponse,
  VerifyOtpResponse,
} from '../../api/types'

function homePathForRoles(roles: string[]): string {
  for (const role of roles) {
    if (ROLE_HOME_PATHS[role]) return ROLE_HOME_PATHS[role]
  }
  return '/notifications'
}

const deviceId = getDeviceId()

type AuthView = 'login' | 'register' | 'forgot'
type RegisterStep = 'phone' | 'phone-code' | 'email' | 'email-code' | 'profile'

function normalizeIndianMobileNumber(value: string) {
  const digits = value.replace(/\D/g, '')
  if (digits.length === 12 && digits.startsWith('91')) return `+${digits}`
  if (digits.length === 10) return `+91${digits}`
  return value.replace(/[\s-]/g, '')
}

function isValidIndianMobileNumber(value: string) {
  return /^\+91[6-9]\d{9}$/.test(normalizeIndianMobileNumber(value))
}

function formatTimer(seconds: number) {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

/**
 * The full auth surface — sign-in, registration, and password recovery.
 *
 * Rendered by `LoginPage` (the `/login` route) and by `AuthModal`. The markup is
 * identical in both; only the surrounding chrome differs, so switching to the
 * popup changed no auth UI or flow. `/login` has to stay a real route: the API
 * client hard-redirects there on session expiry (outside React), the catch-all
 * route lands there, and password reset returns to it.
 */
export function AuthPanel({ onClose }: { onClose?: () => void }) {
  const navigate = useNavigate()
  const { login } = useAuth()

  const [view, setView] = useState<AuthView>('login')
  const [registerStep, setRegisterStep] = useState<RegisterStep>('email')

  const [loginEmail, setLoginEmail] = useState('')
  const [loginPassword, setLoginPassword] = useState('')
  const [forgotEmail, setForgotEmail] = useState('')
  const [forgotSuccess, setForgotSuccess] = useState<string | null>(null)

  const [phoneNumber, setPhoneNumber] = useState('')
  const [phoneOtp, setPhoneOtp] = useState('')
  const [email, setEmail] = useState('')
  const [emailOtp, setEmailOtp] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('')
  const [registrationToken, setRegistrationToken] = useState<string | null>(null)

  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [expiresIn, setExpiresIn] = useState(0)
  const [retryAfter, setRetryAfter] = useState(0)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const [otpChannel, setOtpChannel] = useState<'phone' | 'email'>('email')
  const emailFirst = otpChannel === 'email'
  const registrationIntro = emailFirst
    ? 'Verify your email first, then add your phone number, display name, and password.'
    : 'Verify your phone first, then verify your email, then choose your display name and password.'

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [])

  // Registration OTP channel is server-configured (SMS may be unavailable).
  useEffect(() => {
    fetch(apiUrl('api/v1/auth/registration-config'))
      .then((r) => r.json())
      .then((d) => {
        if (d?.otp_channel === 'email' || d?.otp_channel === 'phone') {
          setOtpChannel(d.otp_channel)
          setRegisterStep((current) => {
            if (current !== 'email' && current !== 'phone') return current
            return d.otp_channel === 'email' ? 'email' : 'phone'
          })
        }
      })
      .catch(() => {})
  }, [])

  function startCountdowns(expiresInSec: number, retryAfterSec: number | null) {
    setExpiresIn(expiresInSec)
    setRetryAfter(retryAfterSec ?? 0)
    if (timerRef.current) clearInterval(timerRef.current)
    timerRef.current = setInterval(() => {
      setExpiresIn((value) => (value <= 0 ? 0 : value - 1))
      setRetryAfter((value) => (value <= 0 ? 0 : value - 1))
    }, 1000)
  }

  function routeAfterLogin(participant: LoginResponse['participant']) {
    onClose?.()
    if (participant.onboarding_status !== 'complete') {
      navigate('/onboarding', { replace: true })
      return
    }
    navigate(homePathForRoles(participant.roles), { replace: true })
  }

  function resetRegisterFlow(nextStep: RegisterStep = emailFirst ? 'email' : 'phone') {
    setRegisterStep(nextStep)
    setPhoneOtp('')
    setEmailOtp('')
    setRegistrationToken(null)
    setExpiresIn(0)
    setRetryAfter(0)
    if (timerRef.current) clearInterval(timerRef.current)
  }

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setForgotSuccess(null)
    setLoading(true)
    try {
      const res = await fetch(apiUrl('api/v1/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: loginEmail.trim().toLowerCase(),
          password: loginPassword,
          device_id: deviceId,
          device_info: { platform: 'web', app_version: '1.0.0' },
        }),
      })
      const body = await res.json().catch(() => ({ message: 'Login failed' }))
      if (!res.ok) {
        setError(body.detail?.message ?? body.message ?? 'Login failed')
        return
      }
      const data = body as LoginResponse
      login(data.access_token, data.participant)
      routeAfterLogin(data.participant)
    } catch {
      setError('Network error. Please check your connection.')
    } finally {
      setLoading(false)
    }
  }

  async function handleForgotPassword(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setForgotSuccess(null)
    setLoading(true)
    try {
      const res = await fetch(apiUrl('api/v1/auth/forgot-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail.trim().toLowerCase() }),
      })
      const body = (await res
        .json()
        .catch(() => ({ message: 'Reset request failed' }))) as ForgotPasswordResponse
      if (!res.ok) {
        setError(
          (body as { detail?: { message?: string } }).detail?.message ??
            body.message ??
            'Reset request failed',
        )
        return
      }
      setForgotSuccess(body.message)
    } catch {
      setError('Network error. Please check your connection.')
    } finally {
      setLoading(false)
    }
  }

  async function handleSendPhoneCode(e?: React.FormEvent) {
    e?.preventDefault()
    setError(null)
    const normalized = normalizeIndianMobileNumber(phoneNumber)
    if (!isValidIndianMobileNumber(normalized)) {
      setError('Enter a valid Indian mobile number, for example +91 98765 43210.')
      return
    }
    setLoading(true)
    try {
      const res = await fetch(apiUrl('api/v1/auth/send-otp'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: normalized, channel: 'phone' }),
      })
      const body = await res.json().catch(() => ({ message: 'Failed to send SMS code' }))
      if (!res.ok) {
        setError(body.detail?.message ?? body.message ?? 'Failed to send SMS code')
        return
      }
      const data = body as SendOtpResponse
      setPhoneNumber(normalized)
      setPhoneOtp('')
      startCountdowns(data.expires_in_seconds, data.retry_after_seconds)
      setRegisterStep('phone-code')
    } catch {
      setError('Network error. Please check your connection.')
    } finally {
      setLoading(false)
    }
  }

  async function handleVerifyPhoneCode(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!/^\d{6}$/.test(phoneOtp)) {
      setError('Enter the 6-digit SMS code.')
      return
    }
    setLoading(true)
    try {
      const res = await fetch(apiUrl('api/v1/auth/verify-otp'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          identifier: normalizeIndianMobileNumber(phoneNumber),
          channel: 'phone',
          otp: phoneOtp,
          device_id: deviceId,
          device_info: { platform: 'web', app_version: '1.0.0' },
        }),
      })
      const body = await res.json().catch(() => ({ message: 'Verification failed' }))
      if (!res.ok) {
        setError(body.detail?.message ?? body.message ?? 'Invalid or expired SMS code')
        return
      }
      const data = body as VerifyOtpResponse
      if (!data.registration_token) {
        setError('Phone verification completed, but registration could not continue.')
        return
      }
      setRegistrationToken(data.registration_token)
      setRegisterStep('email')
      setExpiresIn(0)
      setRetryAfter(0)
    } catch {
      setError('Network error. Please check your connection.')
    } finally {
      setLoading(false)
    }
  }

  async function handleSendEmailCode(e?: React.FormEvent) {
    e?.preventDefault()
    setError(null)
    // In phone-first mode the email step requires a prior phone_verified token.
    // In email-first mode email is the entry channel — no token yet.
    if (!emailFirst && !registrationToken) {
      setError('Phone verification expired. Start registration again.')
      return
    }
    setLoading(true)
    try {
      const res = await fetch(apiUrl('api/v1/auth/send-otp'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          identifier: email.trim().toLowerCase(),
          channel: 'email',
          registration_token: emailFirst ? undefined : registrationToken,
        }),
      })
      const body = await res.json().catch(() => ({ message: 'Failed to send email code' }))
      if (!res.ok) {
        setError(body.detail?.message ?? body.message ?? 'Failed to send email code')
        return
      }
      const data = body as SendOtpResponse
      setEmailOtp('')
      startCountdowns(data.expires_in_seconds, data.retry_after_seconds)
      setRegisterStep('email-code')
    } catch {
      setError('Network error. Please check your connection.')
    } finally {
      setLoading(false)
    }
  }

  async function handleVerifyEmailCode(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!emailFirst && !registrationToken) {
      setError('Phone verification expired. Start registration again.')
      return
    }
    if (!/^\d{6}$/.test(emailOtp)) {
      setError('Enter the 6-digit email code.')
      return
    }
    setLoading(true)
    try {
      const res = await fetch(apiUrl('api/v1/auth/verify-otp'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          identifier: email.trim().toLowerCase(),
          channel: 'email',
          otp: emailOtp,
          device_id: deviceId,
          device_info: { platform: 'web', app_version: '1.0.0' },
          registration_token: emailFirst ? undefined : registrationToken,
        }),
      })
      const body = await res.json().catch(() => ({ message: 'Email verification failed' }))
      if (!res.ok) {
        setError(body.detail?.message ?? body.message ?? 'Invalid or expired email code')
        return
      }
      const data = body as VerifyOtpResponse
      if (!data.registration_token) {
        setError('Email verification completed, but registration could not continue.')
        return
      }
      setRegistrationToken(data.registration_token)
      setRegisterStep('profile')
      setExpiresIn(0)
      setRetryAfter(0)
    } catch {
      setError('Network error. Please check your connection.')
    } finally {
      setLoading(false)
    }
  }

  async function handleRegister(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!registrationToken) {
      setError('Registration session expired. Start again.')
      return
    }
    setLoading(true)
    try {
      const res = await fetch(apiUrl('api/v1/auth/register'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          registration_token: registrationToken,
          display_name: displayName.trim(),
          password,
          device_id: deviceId,
          device_info: { platform: 'web', app_version: '1.0.0' },
          // Email-first mode collects the phone here (stored unverified).
          phone_number: emailFirst ? normalizeIndianMobileNumber(phoneNumber) : undefined,
        }),
      })
      const body = await res.json().catch(() => ({ message: 'Registration failed' }))
      if (!res.ok) {
        setError(body.detail?.message ?? body.message ?? 'Registration failed')
        return
      }
      const data = body as LoginResponse
      login(data.access_token, data.participant)
      routeAfterLogin(data.participant)
    } catch {
      setError('Network error. Please check your connection.')
    } finally {
      setLoading(false)
    }
  }

  return (
    // Option A: pin platform emerald so brand-* CTAs never inherit a sticky vertical.
    <main
      style={platformBrandVars()}
      className="grid w-full max-w-5xl overflow-hidden rounded-[2rem] border border-stone-200 bg-white text-stone-950 shadow-xl md:grid-cols-[1fr_440px]"
    >
      <section className="hidden bg-brand-950 p-10 text-white md:flex md:flex-col md:justify-between">
        <div className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10">
            <Mail className="h-6 w-6" />
          </div>
          <h1 className="mt-8 text-4xl font-bold">OSLE</h1>
          <p className="mt-3 text-sm font-medium uppercase tracking-[0.18em] text-emerald-200">
            Operating System of Local Economy
          </p>
          <p className="mx-auto mt-4 max-w-md text-sm leading-6 text-emerald-50">
            {emailFirst
              ? 'Use email OTP to register while SMS OTP approval is in progress, then sign in with email and password.'
              : 'Verify phone ownership first, verify email before activation, then sign in with email and password.'}
          </p>
        </div>
        <div className="grid gap-3 text-sm text-emerald-50">
          <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
            {emailFirst
              ? 'Registration starts with an email OTP and collects a phone number for later SMS verification.'
              : 'Registration starts with SMS OTP and completes with verified email and password.'}
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
            {emailFirst
              ? 'Returning users sign in with email and password. Phone verification can be completed after SMS is available.'
              : 'Returning users sign in with email and password. Phone stays available for verification and recovery hardening.'}
          </div>
        </div>
      </section>

      {/* When closable, reserve room above the tab row on md+ for the close
          button. Below md the tabs already sit under the compact OSLE header,
          so they clear it without extra padding. */}
      <section className={`relative p-6 sm:p-8 ${onClose ? 'md:pt-16' : ''}`}>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-stone-500 transition-colors hover:bg-stone-100 hover:text-stone-900"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        <div className="mb-8">
          <div className="flex items-center gap-3 md:hidden">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-brand-950 text-white">
              <Mail className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-lg font-bold">OSLE</h1>
              <p className="text-xs text-stone-500">Operating System of Local Economy</p>
            </div>
          </div>

          <div className="mt-8 flex rounded-2xl border border-stone-200 bg-stone-50 p-1 md:mt-0">
            {(['login', 'register', 'forgot'] as const).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => {
                  setError(null)
                  setForgotSuccess(null)
                  setView(option)
                  if (option === 'register') resetRegisterFlow(emailFirst ? 'email' : 'phone')
                }}
                className={`flex-1 rounded-xl px-3 py-2 text-sm font-semibold transition ${
                  view === option ? 'bg-white text-emerald-950 shadow-sm' : 'text-stone-600'
                }`}
              >
                {option === 'login'
                  ? 'Sign in'
                  : option === 'register'
                    ? 'Register'
                    : 'Forgot password'}
              </button>
            ))}
          </div>
        </div>

        {view === 'login' && (
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <p className="text-xs font-semibold uppercase text-emerald-700">Email sign in</p>
              <h2 className="mt-2 text-2xl font-bold text-stone-950">Sign in to OSLE</h2>
              <p className="mt-2 min-h-12 text-sm leading-6 text-stone-600">
                Use your verified email address and password.
              </p>
            </div>

            <div>
              <label htmlFor="login-email" className="text-sm font-medium text-stone-700">
                Email
              </label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
                <input
                  id="login-email"
                  type="email"
                  autoComplete="email"
                  value={loginEmail}
                  onChange={(event) => setLoginEmail(event.target.value)}
                  className="w-full rounded-2xl border border-stone-300 bg-white py-3 pl-10 pr-3 text-sm outline-none transition focus:border-brand-700 focus:ring-2 focus:ring-brand-100"
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label htmlFor="login-password" className="text-sm font-medium text-stone-700">
                Password
              </label>
              <input
                id="login-password"
                type="password"
                autoComplete="current-password"
                value={loginPassword}
                onChange={(event) => setLoginPassword(event.target.value)}
                className="mt-2 w-full rounded-2xl border border-stone-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-brand-700 focus:ring-2 focus:ring-brand-100"
                disabled={loading}
              />
            </div>

            {error && <ErrorBanner message={error} />}

            <button
              type="submit"
              disabled={loading || !loginEmail.trim() || !loginPassword}
              className="flex w-full items-center justify-center gap-2 rounded-2xl bg-brand-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-900 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ShieldCheck className="h-4 w-4" />
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        )}

        {view === 'forgot' && (
          <form onSubmit={handleForgotPassword} className="space-y-5">
            <div>
              <p className="text-xs font-semibold uppercase text-emerald-700">Password recovery</p>
              <h2 className="mt-2 text-2xl font-bold text-stone-950">Reset your password</h2>
              <p className="mt-2 min-h-12 text-sm leading-6 text-stone-600">
                Enter the email address on your OSLE account.
              </p>
            </div>

            <div>
              <label htmlFor="forgot-email" className="text-sm font-medium text-stone-700">
                Email
              </label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
                <input
                  id="forgot-email"
                  type="email"
                  autoComplete="email"
                  value={forgotEmail}
                  onChange={(event) => setForgotEmail(event.target.value)}
                  className="w-full rounded-2xl border border-stone-300 bg-white py-3 pl-10 pr-3 text-sm outline-none transition focus:border-brand-700 focus:ring-2 focus:ring-brand-100"
                  disabled={loading}
                />
              </div>
            </div>

            {error && <ErrorBanner message={error} />}
            {forgotSuccess && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                {forgotSuccess}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !forgotEmail.trim()}
              className="flex w-full items-center justify-center gap-2 rounded-2xl bg-brand-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-900 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Mail className="h-4 w-4" />
              {loading ? 'Sending reset link...' : 'Send reset link'}
            </button>
          </form>
        )}

        {view === 'register' && (
          <div className="space-y-5">
            <div>
              <p className="text-xs font-semibold uppercase text-emerald-700">Registration</p>
              <h2 className="mt-2 text-2xl font-bold text-stone-950">Create your account</h2>
              <p className="mt-2 min-h-12 text-sm leading-6 text-stone-600">{registrationIntro}</p>
            </div>

            {registerStep === 'phone' && (
              <form onSubmit={handleSendPhoneCode} className="space-y-5">
                <div>
                  <label htmlFor="register-phone" className="text-sm font-medium text-stone-700">
                    Mobile number
                  </label>
                  <div className="relative mt-2">
                    <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
                    <input
                      id="register-phone"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      placeholder="+91 98765 43210"
                      value={phoneNumber}
                      onChange={(event) => setPhoneNumber(event.target.value)}
                      className="w-full rounded-2xl border border-stone-300 bg-white py-3 pl-10 pr-3 text-sm outline-none transition focus:border-brand-700 focus:ring-2 focus:ring-brand-100"
                      disabled={loading}
                    />
                  </div>
                </div>

                {error && <ErrorBanner message={error} />}

                <button
                  type="submit"
                  disabled={loading || !isValidIndianMobileNumber(phoneNumber)}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl bg-brand-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-900 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Phone className="h-4 w-4" />
                  {loading ? 'Sending code...' : 'Send SMS code'}
                </button>
              </form>
            )}

            {registerStep === 'phone-code' && (
              <form onSubmit={handleVerifyPhoneCode} className="space-y-5">
                <button
                  type="button"
                  onClick={() => {
                    setError(null)
                    setPhoneOtp('')
                    setRegisterStep('phone')
                  }}
                  className="flex items-center gap-2 text-sm font-medium text-stone-500 hover:text-stone-800"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Change number
                </button>

                <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-sm text-emerald-900">
                  Code sent to <span className="font-semibold">{phoneNumber}</span>
                  {expiresIn > 0 && (
                    <span className="block text-xs text-emerald-700">
                      Expires in {formatTimer(expiresIn)}
                    </span>
                  )}
                </div>

                <div>
                  <label htmlFor="phone-code" className="text-sm font-medium text-stone-700">
                    6-digit SMS code
                  </label>
                  <input
                    id="phone-code"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    value={phoneOtp}
                    onChange={(event) =>
                      setPhoneOtp(event.target.value.replace(/\D/g, '').slice(0, 6))
                    }
                    className="mt-2 w-full rounded-2xl border border-stone-300 bg-white px-4 py-3 text-center text-xl outline-none transition focus:border-brand-700 focus:ring-2 focus:ring-brand-100"
                    disabled={loading}
                  />
                </div>

                {error && <ErrorBanner message={error} />}

                <button
                  type="submit"
                  disabled={loading || phoneOtp.length !== 6}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl bg-brand-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-900 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <ShieldCheck className="h-4 w-4" />
                  {loading ? 'Verifying...' : 'Verify phone'}
                </button>

                <button
                  type="button"
                  onClick={() => handleSendPhoneCode()}
                  disabled={retryAfter > 0 || loading}
                  className="w-full rounded-2xl border border-stone-200 px-4 py-3 text-sm font-semibold text-stone-700 transition hover:bg-stone-50 disabled:cursor-not-allowed disabled:text-stone-400"
                >
                  {retryAfter > 0 ? `Resend code in ${retryAfter}s` : 'Resend SMS code'}
                </button>
              </form>
            )}

            {registerStep === 'email' && (
              <form onSubmit={handleSendEmailCode} className="space-y-5">
                {!emailFirst && (
                  <button
                    type="button"
                    onClick={() => {
                      setError(null)
                      setEmail('')
                      setRegisterStep('phone-code')
                    }}
                    className="flex items-center gap-2 text-sm font-medium text-stone-500 hover:text-stone-800"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back to phone verification
                  </button>
                )}

                <div>
                  <label htmlFor="register-email" className="text-sm font-medium text-stone-700">
                    Email
                  </label>
                  <div className="relative mt-2">
                    <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
                    <input
                      id="register-email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      className="w-full rounded-2xl border border-stone-300 bg-white py-3 pl-10 pr-3 text-sm outline-none transition focus:border-brand-700 focus:ring-2 focus:ring-brand-100"
                      disabled={loading}
                    />
                  </div>
                </div>

                {error && <ErrorBanner message={error} />}

                <button
                  type="submit"
                  disabled={loading || !email.trim()}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl bg-brand-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-900 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Mail className="h-4 w-4" />
                  {loading ? 'Sending code...' : 'Send email code'}
                </button>
              </form>
            )}

            {registerStep === 'email-code' && (
              <form onSubmit={handleVerifyEmailCode} className="space-y-5">
                <button
                  type="button"
                  onClick={() => {
                    setError(null)
                    setEmailOtp('')
                    setRegisterStep('email')
                  }}
                  className="flex items-center gap-2 text-sm font-medium text-stone-500 hover:text-stone-800"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Change email
                </button>

                <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-sm text-emerald-900">
                  Code sent to <span className="font-semibold">{email}</span>
                  {expiresIn > 0 && (
                    <span className="block text-xs text-emerald-700">
                      Expires in {formatTimer(expiresIn)}
                    </span>
                  )}
                </div>

                <div>
                  <label htmlFor="email-code" className="text-sm font-medium text-stone-700">
                    6-digit email code
                  </label>
                  <input
                    id="email-code"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    value={emailOtp}
                    onChange={(event) =>
                      setEmailOtp(event.target.value.replace(/\D/g, '').slice(0, 6))
                    }
                    className="mt-2 w-full rounded-2xl border border-stone-300 bg-white px-4 py-3 text-center text-xl outline-none transition focus:border-brand-700 focus:ring-2 focus:ring-brand-100"
                    disabled={loading}
                  />
                </div>

                {error && <ErrorBanner message={error} />}

                <button
                  type="submit"
                  disabled={loading || emailOtp.length !== 6}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl bg-brand-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-900 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <ShieldCheck className="h-4 w-4" />
                  {loading ? 'Verifying...' : 'Verify email'}
                </button>
              </form>
            )}

            {registerStep === 'profile' && (
              <form onSubmit={handleRegister} className="space-y-5">
                <button
                  type="button"
                  onClick={() => {
                    setError(null)
                    setRegisterStep('email-code')
                  }}
                  className="flex items-center gap-2 text-sm font-medium text-stone-500 hover:text-stone-800"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to email verification
                </button>

                <div>
                  <label htmlFor="display-name" className="text-sm font-medium text-stone-700">
                    Display name
                  </label>
                  <input
                    id="display-name"
                    type="text"
                    value={displayName}
                    onChange={(event) => setDisplayName(event.target.value)}
                    className="mt-2 w-full rounded-2xl border border-stone-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-brand-700 focus:ring-2 focus:ring-brand-100"
                    disabled={loading}
                  />
                </div>

                {emailFirst && (
                  <div>
                    <label htmlFor="register-phone" className="text-sm font-medium text-stone-700">
                      Phone number
                    </label>
                    <input
                      id="register-phone"
                      type="tel"
                      autoComplete="tel"
                      value={phoneNumber}
                      onChange={(event) => setPhoneNumber(event.target.value)}
                      placeholder="10-digit mobile number"
                      className="mt-2 w-full rounded-2xl border border-stone-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-brand-700 focus:ring-2 focus:ring-brand-100"
                      disabled={loading}
                    />
                    <p className="mt-2 text-xs text-stone-500">
                      SMS OTP verification will be enabled later while business registration is in
                      progress.
                    </p>
                  </div>
                )}

                <div>
                  <label htmlFor="register-password" className="text-sm font-medium text-stone-700">
                    Password
                  </label>
                  <input
                    id="register-password"
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    className="mt-2 w-full rounded-2xl border border-stone-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-brand-700 focus:ring-2 focus:ring-brand-100"
                    disabled={loading}
                  />
                  <p className="mt-2 text-xs text-stone-500">
                    Use at least 8 characters with at least one letter and one number.
                  </p>
                </div>

                {error && <ErrorBanner message={error} />}

                <button
                  type="submit"
                  disabled={
                    loading ||
                    !displayName.trim() ||
                    !password ||
                    (emailFirst && !phoneNumber.trim())
                  }
                  className="flex w-full items-center justify-center gap-2 rounded-2xl bg-brand-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-900 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <ShieldCheck className="h-4 w-4" />
                  {loading ? 'Creating account...' : 'Create account'}
                </button>
              </form>
            )}
          </div>
        )}

        <p className="mt-8 text-xs leading-5 text-stone-500">
          By continuing, you agree to OSLE account security and consent requirements.
        </p>
      </section>
    </main>
  )
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      {message}
    </div>
  )
}
